export const OTHER_METHOD = "OTHER";
export const CUSTOMER_VISIT_ID = "customer";
export const REMAINING_COLOR = "#d9d9d9";
export const REMAINING_TEXT = "#ff4d4f";

// Stable color rotation; methods we expect get fixed brand-ish colors,
// anything else falls back to a deterministic pick from the palette.
// Keys are post-normalization (stripped of non-alphanumerics) so the lookup
// in `colorForMethod` is order-insensitive to separators or casing.
export const METHOD_COLORS = {
  CASH: "#52c41a",
  TRUEMONEY: "#1677ff",
  QRCODE: "#722ed1",
  CARD: "#13c2c2",
  ONLINE: "#2f54eb",
};
export const FALLBACK_PALETTE = ["#fa8c16", "#eb2f96", "#a0d911", "#faad14", "#08979c"];

export const colorForMethod = (method, fallbackIndex) => {
  const upper = (method || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (METHOD_COLORS[upper]) return METHOD_COLORS[upper];
  return FALLBACK_PALETTE[fallbackIndex % FALLBACK_PALETTE.length];
};

export const prettyMethodLabel = (method) => {
  if (!method) return OTHER_METHOD;
  return method
    .toString()
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const formatAmount = (amount) => {
  const parsed = Number(amount);
  const numeric = Number.isFinite(parsed) ? parsed : 0;
  // Pinned to en-US so output is deterministic regardless of host locale
  // (matters for both CI snapshots and consistent driver-facing display).
  return numeric.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const getPaymentMethodKey = (payment) => {
  const fromAdditionalInfo = payment?.additionalInfo?.paymentMode;
  const fromInstrument = payment?.instrument?.instrumentName;
  return (fromAdditionalInfo || fromInstrument || OTHER_METHOD).toString();
};

// Aggregation key — match the same canonicalisation as `colorForMethod` so
// API variants like "True-Money", "TRUE_MONEY", and "TRUEMONEY" land in the
// same bucket instead of three separate rows.
export const normalizeMethodKey = (method) => {
  const stripped = (method || "").toString().toUpperCase().replace(/[^A-Z0-9]/g, "");
  return stripped || OTHER_METHOD;
};

const toFiniteAmount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

// Locus ships customer payments under a few different shapes depending on the
// endpoint. Returns the customer visit's `payments` object (with totalAmount,
// pendingAmount, paymentRequired, payments[]) for a task.
export const getCustomerPaymentsObject = (task) => {
  const fromCustomerVisit = task?.customerVisit?.minimalPayments;
  if (fromCustomerVisit?.payments) return fromCustomerVisit;
  const visits = task?.visits || task?.taskGraph?.visits || [];
  const customerVisit = visits.find((visit) => visit?.id === CUSTOMER_VISIT_ID);
  return customerVisit?.payments || null;
};

// The LOTR `alltours` response ships tours and tasks as two top-level arrays
// (tour metadata is nested under tourId/status/tourName), unlike tour-search
// which nests tasks inside each tour. Reshape it into the flat
// { id, name, tourStatus, tasks[] } the dropdown and `buildSummary` expect, so
// the rest of the component (and the mock path) stay endpoint-agnostic.
export const tourIdOf = (tour) => tour?.tourId?.tourId || tour?.tourId || tour?.id;

export const normalizeRiderTours = (data) => {
  const tours = data?.tours || [];
  const tasks = data?.tasks || [];
  return tours.map((tour) => {
    const id = tourIdOf(tour);
    return {
      id,
      name: tour?.providedTourName || tour?.tourName || tour?.name || id,
      tourStatus: tour?.status?.status || tour?.tourStatus || null,
      tasks: tasks.filter((task) => tourIdOf(task) === id),
    };
  });
};

export const buildSummary = (tour) => {
  const buckets = new Map();
  let podTotal = 0;
  let prepaidTotal = 0;

  (tour?.tasks || []).forEach((task) => {
    const paymentsObj = getCustomerPaymentsObject(task);
    if (!paymentsObj) return;

    const taskTotal = toFiniteAmount(paymentsObj?.totalAmount?.amount);
    const isPrepaid = paymentsObj?.paymentRequired === false;

    if (isPrepaid) {
      prepaidTotal += taskTotal;
      return;
    }
    podTotal += taskTotal;

    (paymentsObj?.payments || []).forEach((payment) => {
      if (payment?.status !== "SUCCESS") return;
      const rawKey = getPaymentMethodKey(payment);
      const key = normalizeMethodKey(rawKey);
      const amountValue = toFiniteAmount(payment?.actualAmount?.amount);
      const existing = buckets.get(key) || { method: rawKey, total: 0, count: 0 };
      existing.total += amountValue;
      existing.count += 1;
      buckets.set(key, existing);
    });
  });

  const breakdown = Array.from(buckets.values())
    .sort((a, b) => b.total - a.total)
    .map((entry, index) => ({
      ...entry,
      label: `${prettyMethodLabel(entry.method)} (Collected)`,
      color: colorForMethod(entry.method, index),
    }));

  const collected = breakdown.reduce((sum, b) => sum + b.total, 0);
  const remaining = Math.max(0, podTotal - collected);

  return { breakdown, podTotal, prepaidTotal, collected, remaining };
};

// Visual-only demo payload — used when ?mock=1 is on the URL. Mirrors the
// numbers in the design mock so we can validate the populated layout without
// real POD collections in devo.
export const DEMO_TOUR = {
  id: "demo-tour",
  name: "Tour 4",
  tourStatus: "STARTED",
  tasks: [
    {
      taskGraph: {
        visits: [
          {
            id: "customer",
            payments: {
              paymentRequired: true,
              totalAmount: { amount: 1638, currency: "THB", symbol: "THB" },
              pendingAmount: { amount: 845.75, currency: "THB", symbol: "THB" },
              payments: [
                {
                  status: "SUCCESS",
                  additionalInfo: { paymentMode: "CASH" },
                  actualAmount: { amount: 500, currency: "THB", symbol: "THB" },
                },
                {
                  status: "SUCCESS",
                  additionalInfo: { paymentMode: "TRUE_MONEY" },
                  actualAmount: { amount: 292.25, currency: "THB", symbol: "THB" },
                },
              ],
            },
          },
        ],
      },
    },
    {
      taskGraph: {
        visits: [
          {
            id: "customer",
            payments: {
              paymentRequired: false,
              totalAmount: { amount: 500, currency: "THB", symbol: "THB" },
              payments: [],
            },
          },
        ],
      },
    },
  ],
};
